import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFCoreEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withMetadata,
    withSearchFilter,
} from '@pf/shared-utility';
import {
    UserPositionCreate,
    UserPositionDto,
    UserPositionRead,
    UserPositionSearchParams,
} from '../../entities/user-position.dto';
import {
    ITypeEntityFragmentSymbol,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';

const symbols = entitySymbols(PFCoreEntities.UserPosition);

withDefaultEntityMetadata<Omit<UserPositionDto, 'name'>>(symbols.dto, {
    userPositionTypeName: String,
    userPositionTypeId: String,
    enteredDateTime: String,
    exitedDateTime: String,
    status: String,
    timeInPosition: Number,
    context: Object,
    resourceAuth: ResourceAuthSymbol,
    userId: Object,
    userFirstName: String,
    userLastName: String,
    userEmail: String,
    locationId: Object,
    locationName: String,
});

withDefaultEntityMetadata<UserPositionRead>(symbols.entity, {
    userPositionType: ITypeEntityFragmentSymbol,
    enteredDateTime: String,
    exitedDateTime: String,
    status: String,
    timeInPosition: Number,
    contextJson: Object,
    resourceAuth: ResourceAuthSymbol,
    user: Object,
    location: Object,
});

withMetadata<UserPositionCreate>(symbols.createBody, {
    userPositionTypeId: String,
    enteredDateTime: String,
    exitedDateTime: String,
    status: String,
    contextJson: Object,
    userId: String,
    locationId: String,
    resourceAuth: ResourceAuthSymbol,
});

withDefaultSearchParamsMetadata<UserPositionSearchParams>(
    symbols.searchParams,
    {
        userPositionTypeId: String,
        status: String,
        userId: String,
        locationId: String,
    }
);

export const UserPositionMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<UserPositionRead, UserPositionDto>(
            mapper,
            symbols.entity,
            symbols.dto,
            forMember(
                dest => dest.userPositionTypeId,
                mapFrom(src => src.userPositionType?.id)
            ),
            forMember(
                dest => dest.userPositionTypeName,
                mapFrom(src => src.userPositionType?.name)
            ),
            forMember(
                dest => dest.context,
                mapFrom(src => src.contextJson)
            )
        );
        createMap<UserPositionDto, UserPositionRead>(
            mapper,
            symbols.dto,
            symbols.entity,
            forMember(
                dest => dest.contextJson,
                mapFrom(src => src.context)
            )
        );
        createMap<UserPositionDto, UserPositionCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                dest => dest.contextJson,
                mapFrom(src => src.context)
            )
        );
        createMap<IQueryParams<UserPositionDto>, UserPositionSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('status'),
            withSearchFilter('userPositionTypeId'),
            withSearchFilter('userId'),
            withSearchFilter('locationId'),
            forMember(
                d => d.search,
                mapFrom(s => {
                    return s.searchValue;
                })
            ),
            ...withDefaultSearchParamsMappingConfiguration()
        );
    },
};
